import * as React from "react"
import * as Yup from "yup"
import Form, { FormElement } from "@modules/form/components/Form"
import {
  Button,
  InputConnectedField,
  SelectConnectedField,
  StyledModalActions,
  Text,
  ThemeCss,
} from "gatsby-interface"
import {
  dataDogIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { dataDogRegionNames } from "../constants"
import { DataDogRegion } from "../types"
import { LogDrainIntegration } from "@modules/graphql/types"
import {
  useCreateLogDrainIntegrationMutation,
  useUpdateLogDrainIntegrationMutation,
} from "@modules/logDrain/shared/queries.generated"
import { isProperlyConnected } from "@modules/logDrain/shared/helpers"

type FormValues = {
  region: string
  apiKey: string
}

const validationSchema = Yup.object().shape<FormValues>({
  region: Yup.string().required(uiText.messages.validationIsRequired),
  apiKey: Yup.string().required(uiText.messages.validationIsRequired),
})
type DataDogLogDrainWizardProps = {
  logDrain: LogDrainIntegration
  siteId: string
  handleSuccess: (obj: { successText: string }) => void
  handleFailure: (error: Error) => void
  onCancel: () => void
}

export function DataDogLogDrainWizard({
  siteId,
  logDrain,
  handleSuccess,
  handleFailure,
  onCancel,
}: DataDogLogDrainWizardProps) {
  const config = logDrain?.config
  const [create] = useCreateLogDrainIntegrationMutation()
  const [update] = useUpdateLogDrainIntegrationMutation()
  const [editing, setEditing] = React.useState<boolean>(false)

  React.useEffect(() => {
    setEditing(isProperlyConnected(logDrain))
  }, [setEditing])
  return (
    <Form<FormValues>
      css={formCss}
      validationSchema={validationSchema}
      initialValues={{
        region: config?.region ?? DataDogRegion.US1,
        apiKey: config?.apiKey ?? ``,
      }}
      onSubmit={(values, { setSubmitting }) => {
        if (editing) {
          update({
            variables: {
              siteId,
              config: values,
              integrationId: logDrain.id,
            },
          })
            .then(({ data }) => {
              if (data?.updateLogDrainIntegration?.success) {
                handleSuccess({
                  successText: text.messages.updated,
                })
              } else {
                handleFailure({
                  message: data?.updateLogDrainIntegration?.message ?? ``,
                  name: data?.updateLogDrainIntegration?.errorType ?? ``,
                })
              }
            })
            .catch(handleFailure)
        } else {
          create({
            variables: {
              siteId,
              logDrainProvider: logDrain.logDrainProvider,
              config: values,
            },
          })
            .then(({ data }) => {
              if (data?.createLogDrainIntegration?.success) {
                handleSuccess({
                  successText: text.messages.created,
                })
              } else {
                handleFailure({
                  message: data?.createLogDrainIntegration?.message ?? ``,
                  name: data?.createLogDrainIntegration?.errorType ?? ``,
                })
              }
            })
            .catch(handleFailure)
        }
        setSubmitting(false)
      }}
    >
      {({ isSubmitting }) => (
        <FormElement applySpacing noValidate>
          <Text css={{ margin: 0 }}>{text.messages.introText}</Text>
          <SelectConnectedField
            name="region"
            options={Object.entries(dataDogRegionNames).map(
              ([value, label]) => {
                return { value, label }
              }
            )}
            label={text.labels.region}
            required
          />
          <InputConnectedField
            label={text.labels.ApiKey}
            name="apiKey"
            required
            type="text"
          />
          <StyledModalActions>
            <Button variant="SECONDARY" type="reset" onClick={onCancel}>
              {uiText.actions.cancel}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {editing ? text.actions.update : text.actions.connect}
            </Button>
          </StyledModalActions>
        </FormElement>
      )}
    </Form>
  )
}

/* Styles */
const formCss: ThemeCss = theme => {
  return {
    paddingBottom: theme.space[4],
  }
}
